/**
 * WelcomeOnboardingModal.jsx
 * A 3-step onboarding experience for first-time users.
 * Steps: 1. Create your first deal, 2. Explore the Exchange, 3. Add a partner role
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  FileText,
  Zap,
  Users,
  ArrowRight,
  ChevronRight,
  Sparkles,
  CheckCircle,
  Building2,
  DollarSign,
  Briefcase
} from "lucide-react";
import { Button } from "../ui/button";

const STEPS = [
  {
    id: 1,
    title: "Create Your First Deal",
    subtitle: "Start building your pipeline",
    description: "Analyze properties, run AI guidance, track opportunities through the entire lifecycle — from lead to closing.",
    icon: FileText,
    cta: "Add a Deal",
    ctaPath: "/deals",
    features: ["AI-powered deal analysis", "Strategy recommendations", "Profit projections"],
    gradient: "from-blue-500/20 to-indigo-500/20",
    borderGlow: "border-blue-500/30",
    iconBg: "bg-blue-500/10",
    iconColor: "text-blue-400"
  },
  {
    id: 2,
    title: "Explore the Exchange",
    subtitle: "Connect with service providers",
    description: "Post funding requests, find contractors, connect with realtors and title companies — all in one marketplace.",
    icon: Zap,
    cta: "Open Exchange",
    ctaPath: "/network/exchange",
    features: ["Post service requests", "Receive competitive bids", "Match with providers"],
    gradient: "from-[#D4AF37]/20 to-amber-500/20",
    borderGlow: "border-[#D4AF37]/30",
    iconBg: "bg-[#D4AF37]/10",
    iconColor: "text-[#D4AF37]"
  },
  {
    id: 3,
    title: "Unlock Partner Portals",
    subtitle: "Earn from multiple sides of the deal",
    description: "Add lender, contractor, realtor, or title roles to your account. Each unlocks a dedicated portal with new deal flow.",
    icon: Users,
    cta: "Add a Role",
    ctaPath: "/upgrade",
    features: ["Lender marketplace access", "Contractor job board", "Multi-role earnings"],
    gradient: "from-emerald-500/20 to-teal-500/20",
    borderGlow: "border-emerald-500/30",
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-400"
  }
];

export default function WelcomeOnboardingModal({ isOpen, onClose, userName }) {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  const handleCTA = () => {
    const step = STEPS[currentStep];
    onClose();
    navigate(step.ctaPath);
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const handleSkip = () => {
    onClose();
  };

  if (!isOpen) return null;

  const step = STEPS[currentStep];
  const StepIcon = step.icon;
  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={handleSkip}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative z-10 w-full max-w-lg mx-4"
          data-testid="welcome-onboarding-modal"
        >
          {/* Card */}
          <div className={`bg-[#0A0A0A] border ${step.borderGlow} rounded-2xl overflow-hidden shadow-2xl`}>
            {/* Header Glow */}
            <div className={`h-1.5 bg-gradient-to-r ${step.gradient}`} />

            {/* Close Button */}
            <button
              onClick={handleSkip}
              className="absolute top-4 right-4 p-1.5 text-white/40 hover:text-white transition-colors z-20"
              data-testid="onboarding-close-btn"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Content */}
            <div className="p-8">
              {/* Welcome Badge */}
              {currentStep === 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 mb-6"
                >
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full">
                    <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                    <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">
                      Welcome to DealForge{userName ? `, ${userName.split(' ')[0]}` : ''}
                    </span>
                  </div>
                </motion.div>
              )}

              {/* Step Progress */}
              <div className="flex items-center gap-2 mb-6">
                {STEPS.map((s, idx) => (
                  <div
                    key={s.id}
                    className={`flex-1 h-1 rounded-full transition-all duration-300 ${
                      idx <= currentStep ? 'bg-[#D4AF37]' : 'bg-white/10'
                    }`}
                  />
                ))}
              </div>

              {/* Step Content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Icon */}
                  <div className={`w-14 h-14 ${step.iconBg} rounded-xl flex items-center justify-center mb-5`}>
                    <StepIcon className={`w-7 h-7 ${step.iconColor}`} />
                  </div>

                  {/* Title */}
                  <div className="mb-4">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-1">
                      Step {currentStep + 1} of {STEPS.length}
                    </p>
                    <h2 className="text-2xl font-bold text-white mb-1">{step.title}</h2>
                    <p className="text-white/60 text-sm">{step.subtitle}</p>
                  </div>

                  {/* Description */}
                  <p className="text-white/70 text-sm leading-relaxed mb-6">
                    {step.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-2 mb-8">
                    {step.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <CheckCircle className={`w-4 h-4 ${step.iconColor} flex-shrink-0`} />
                        <span className="text-sm text-white/80">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleCTA}
                  className={`flex-1 h-11 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-lg`}
                  data-testid="onboarding-cta-btn"
                >
                  {step.cta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                <Button
                  onClick={handleNext}
                  variant="outline"
                  className="h-11 px-4 border-white/10 text-white/70 hover:text-white hover:bg-white/5 rounded-lg"
                  data-testid="onboarding-next-btn"
                >
                  {isLastStep ? "Done" : "Next"}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              {/* Skip Link */}
              <button
                onClick={handleSkip}
                className="w-full mt-4 text-center text-xs text-white/30 hover:text-white/50 transition-colors"
                data-testid="onboarding-skip-btn"
              >
                Skip onboarding
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
